import { getStoredToken } from '../authApi';
import { getServerUrl } from '../updateChannel';

const baseUrl = () => getServerUrl();

async function authHeaders() {
  const token = await getStoredToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// GET /cosmetics → { packs: [...], owned: [cardId, ...] }
// `packs` is the static catalog of buyable cosmetic packs; `owned` is
// the array of card unique_ids the player has unlocked as cosmetics.
export async function fetchCosmetics() {
  const res = await fetch(`${baseUrl()}/cosmetics`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load cosmetics');
  return res.json();
}

// POST /cosmetics/buy-pack { packId, cardId }. Server-authoritative
// purchase. The caller rolls the random card from the keyword pool
// (the sorcery card db lives client-side); this commits the buy.
// Throws on any server-side rejection; the error's `reason` field
// carries the structured reason so the UI can render a clear toast.
export async function buyCosmeticPack(packId, cardId) {
  const res = await fetch(`${baseUrl()}/cosmetics/buy-pack`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ packId, cardId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body?.ok) {
    const reason = body?.reason || 'unknown';
    const err = new Error(`Cosmetic pack purchase failed: ${reason}`);
    err.reason = reason;
    throw err;
  }
  return body;
}

// POST /cosmetics/claim-starter { cosmeticId? }. One-time free starter
// playmat pick during onboarding. `cosmeticId` is omitted for the free
// vanilla mat (it has no entitlement). The server grants the chosen
// mat free and flips the one-time `starterPlaymatClaimed` gate. Throws
// on rejection with `reason` set.
export async function claimStarterPlaymat(cosmeticId) {
  const res = await fetch(`${baseUrl()}/cosmetics/claim-starter`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ cosmeticId: cosmeticId || null }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body?.ok) {
    const reason = body?.reason || body?.error || 'unknown';
    const err = new Error(`Starter playmat claim failed: ${reason}`);
    err.reason = reason;
    throw err;
  }
  return body;
}

// POST /season-rewards/redeem-voucher { mailId, packId, cardId }.
// claim a top-10 season-voucher mail for a free portrait pack. The
// caller rolls the card client-side from the chosen pack's pool
// (matching the paid /cosmetics/buy-pack flow); the server validates
// the voucher mail is real + unclaimed + addressed to the caller, then
// grants the cosmetic and marks the mail claimed. Throws on rejection
// with `reason` set to the server's structured rejection reason.
export async function redeemSeasonVoucher({ mailId, packId, cardId }) {
  const res = await fetch(`${baseUrl()}/season-rewards/redeem-voucher`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ mailId, packId, cardId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const reason = body?.error || 'unknown';
    const err = new Error(`Voucher redemption failed: ${reason}`);
    err.reason = reason;
    throw err;
  }
  return body;
}
